import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import { useContext } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import ProfileCoachingInsights from "@apps/profile/overview-tab/components/profile-coaching-insights";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNullWithStringId } from "@helpers/helpers";
import { isNextMeetingInGroup } from "@helpers/meetings";

import { createTopicInMeeting } from "../helpers";
import DetectedOneononeBanner from "./detected-oneonone-banner";
import { MeetingViewEnum } from "./header-constants";
import { MeetingPageContext } from "./meeting-page-context";
import MeetingFinalizeButton from "./meeting-sidebar/meeting-finalize";
import { MeetingVideoConferenceImage } from "./meeting/video-conference-button";
import { getMeetingVideoConferenceName } from "./meeting/video-conference-helpers";
import PublishMeetingButton from "./publish-meeting-button";
import MeetingConvertToReview1on1BannerWithSentry from "./review/convert-to-review-1-on-1-banner";
import UndiscussedTopicsBanner from "./undiscussed-topics-banner";

const MeetingDetails = ({
  meeting,
  onChangeMeetingView,
  meetingView,
  onTopicsChanged,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  meetingView: MeetingViewEnum;
  onChangeMeetingView: (option: ToggleButtonGroupType<MeetingViewEnum>) => void;
  onTopicsChanged?: () => void;
}) => {
  const { isInSidebar, isInExtension } = useContext(MeetingPageContext);
  const meetingGroup = meeting.meetingGroup;

  if (!meetingGroup) return null;

  const participants = assertEdgesNonNullWithStringId(meeting.participants);
  const participantUsers = compact(participants.map(({ user }) => user));
  // const facilitator = meeting?.meetingGroup?.facilitator || participantUsers[0];
  const subject = participantUsers.find(
    ({ id }) => id === meeting?.subject?.id,
  );
  const oneOnOneSubject = meetingGroup?.isFormalOneonone ? subject : null;

  const handleClickAddTopic = () => {
    createTopicInMeeting({ meeting });
  };

  return (
    <>
      <div
        className={classNames("flex flex-col gap-4", !isInExtension && "pb-2")}
      >
        <DetectedOneononeBanner meeting={meeting} />

        <UndiscussedTopicsBanner
          meeting={meeting}
          onTopicsChanged={onTopicsChanged}
        />

        {waffle.flag_is_active("coaching-insights") &&
          meeting.meetingGroup.isFormalOneonone &&
          isNextMeetingInGroup({
            previousMeeting: {
              startDatetime: meeting.previousMeeting?.startDatetime,
            },
            startDatetime: meeting.startDatetime,
          }) &&
          oneOnOneSubject && (
            <ProfileCoachingInsights
              profile={oneOnOneSubject}
              alwaysShowPanel={false}
            />
          )}

        <div className="@container/toggle-button-group mt-4">
          <div className="flex gap-4 items-center justify-between">
            <div
              className={classNames(
                "flex items-center text-sm text-gray-600",
                meeting.canUpdate?.permission ? "gap-2" : "gap-4",
              )}
            >
              <ToggleButtonGroup<MeetingViewEnum>
                theme={ToggleButtonGroupTheme.separatedWhiteButtons}
                buttons={compact([
                  meeting.hasPreviousMeetings &&
                    meeting.previousMeeting?.id && {
                      value: MeetingViewEnum.previousMeetingSummary,
                      title: MeetingViewEnum.previousMeetingSummary,
                      active:
                        MeetingViewEnum.previousMeetingSummary === meetingView,
                    },
                  {
                    value: MeetingViewEnum.notes,
                    title: MeetingViewEnum.notes,
                    active: MeetingViewEnum.notes === meetingView,
                  },
                  {
                    value: MeetingViewEnum.summary,
                    title: MeetingViewEnum.summary,
                    active: MeetingViewEnum.summary === meetingView,
                  },
                ])}
                onClick={onChangeMeetingView}
              />
            </div>
            <div className="flex gap-[10px] items-center">
              {!isInExtension && meeting.videoConferenceUrl && (
                <Button
                  className={classNames("gap-2 shrink-0")}
                  tooltip={`Join ${getMeetingVideoConferenceName({
                    meeting,
                    compact: false,
                  })}`}
                  url={meeting.videoConferenceUrl}
                  target="_blank"
                  size={ButtonSize.medium}
                  theme={buttonTheme.text}
                >
                  <MeetingVideoConferenceImage
                    meeting={meeting}
                    className="h-5 w-5"
                  />
                  {!isInSidebar && (
                    <span className="hidden @lg/layout-main:inline shrink-0">
                      <span className="hidden @2xl/layout-main:inline shrink-0">
                        Join{" "}
                      </span>
                      {getMeetingVideoConferenceName({
                        meeting,
                        compact: true,
                      })}
                    </span>
                  )}
                </Button>
              )}
              <Button
                className={classNames("gap-2 shrink-0")}
                size={ButtonSize.medium}
                theme={buttonTheme.text}
                onClick={handleClickAddTopic}
              >
                <TopicflowIcon type="plus" className="h-4 w-4" />
                Topic
              </Button>
              <MeetingFinalizeButton
                meeting={meeting}
                buttonProps={{ size: ButtonSize.medium }}
              />
              {meeting.draft && (
                <PublishMeetingButton
                  meetingId={meeting.id}
                  meetingGroupId={meeting.meetingGroupId}
                  buttonProps={{
                    size: ButtonSize.medium,
                    theme: buttonTheme.primary,
                  }}
                >
                  Publish Meeting
                </PublishMeetingButton>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-4 empty:hidden text-sm text-gray-900">
            {meeting.meetingGroup.isFormalOneonone &&
              meeting.canUpdate?.permission &&
              meeting.assessmentDeliveriesMatching1on1Template.edges.length ===
                0 && (
                <MeetingConvertToReview1on1BannerWithSentry meeting={meeting} />
              )}
          </div>
        </div>
      </div>
    </>
  );
};

const MeetingDetailsWithSentry = withErrorBoundary(MeetingDetails, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingDetailsWithSentry;
